/**
 * Coldest PDP — Desktop color-swatch grid collapse.
 * The Variant Swatch King "Color" option is laid out (via CSS) as a large
 * multi-column grid. When there are more than ~2 rows of designs we collapse
 * it to ~2.5 rows and add a "See all N designs" toggle.
 *
 * VSK re-renders its swatch <ul> repeatedly after first paint (and on variant
 * change). So the collapse is driven entirely from the STABLE host element
 * (the [option-name="Color"] container): JS only sets data-cro-collapsible,
 * data-cro-expanded and a --cro-swatch-max custom property on it — the actual
 * clipping is a CSS rule keyed off those, so re-rendering the <ul>'s children
 * can never undo it. (Mobile keeps its single-row scroller from coldest-global.css.)
 */
(function () {
  'use strict';

  var DESKTOP_MIN = 768;
  var COLS = 5;
  var FULL_ROWS = 2;        // 2 full rows…
  var PEEK = 0.5;           // …+ a half-row peek = 2.5 rows
  var MIN_TO_COLLAPSE = 11; // more than ~2 rows of 5 → worth collapsing

  function findList() {
    var uls = document.querySelectorAll(
      '.swatches [option-name="Color"] ul.swatch-view, ' +
      '.swatches [data-option-name="Color"] ul.swatch-view, ' +
      'variant-swatch-king ul.swatch-view'
    );
    var best = null, bestN = 0;
    uls.forEach(function (ul) {
      var n = ul.querySelectorAll('li.swatch-view-item').length;
      if (n > bestN) { bestN = n; best = ul; }
    });
    return best;
  }

  function hostOf(ul) {
    return (ul && ul.closest('[option-name="Color"], [data-option-name="Color"]')) ||
           document.querySelector('.swatches [option-name="Color"], .swatches [data-option-name="Color"]');
  }

  /* Exact 2.5-row height from the live layout → CSS custom property on host. */
  function measure(h, ul) {
    var items = ul.querySelectorAll('li.swatch-view-item');
    var first = items[0];
    if (!first || !first.getBoundingClientRect().height) {
      requestAnimationFrame(function () { measure(h, ul); });
      return;
    }
    var tile = first.getBoundingClientRect().height;
    var ulTop = ul.getBoundingClientRect().top;
    var row3 = items[COLS * FULL_ROWS];
    var max;
    if (row3) {
      max = Math.round((row3.getBoundingClientRect().top - ulTop) + tile * PEEK);
    } else {
      var cs = getComputedStyle(ul);
      var gap = parseFloat(cs.rowGap || cs.gap || '12') || 12;
      max = Math.round(tile * FULL_ROWS + gap * FULL_ROWS + tile * PEEK);
    }
    h.style.setProperty('--cro-swatch-max', max + 'px');
  }

  function ensureButton(h, ul, total) {
    var btn = h.querySelector('.cro-swatch-seeall');
    if (!btn) {
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cro-swatch-seeall';
      btn.addEventListener('click', function () {
        var expanded = h.getAttribute('data-cro-expanded') === '1';
        h.setAttribute('data-cro-expanded', expanded ? '0' : '1');
        var t = btn.getAttribute('data-total') || total;
        btn.textContent = expanded ? ('See all ' + t + ' designs') : 'Show less';
      });
      if (ul.parentNode) ul.parentNode.insertBefore(btn, ul.nextSibling);
      else h.appendChild(btn);
    }
    btn.setAttribute('data-total', total);
    var expanded = h.getAttribute('data-cro-expanded') === '1';
    var want = expanded ? 'Show less' : ('See all ' + total + ' designs');
    if (btn.textContent !== want) btn.textContent = want;
  }

  function render() {
    if (window.innerWidth < DESKTOP_MIN) return false;
    var ul = findList();
    if (!ul) return false;
    var h = hostOf(ul);
    if (!h) return false;
    var total = ul.querySelectorAll('li.swatch-view-item').length;
    if (!total) return false;
    if (total <= MIN_TO_COLLAPSE) {
      if (h.getAttribute('data-cro-collapsible') === '1') h.removeAttribute('data-cro-collapsible');
      return true;
    }
    if (h.getAttribute('data-cro-collapsible') !== '1') h.setAttribute('data-cro-collapsible', '1');
    measure(h, ul);
    ensureButton(h, ul, total);
    return true;
  }

  /* Mobile: the color row is a horizontal scroller (coldest-global.css #30b).
     Bring the SELECTED swatch into view on load — VSK renders at scrollLeft 0,
     so a selected design deep in the row starts hidden. Only nudges while the
     row is still untouched (scrollLeft 0) so it never fights the user, and
     only within the first few seconds (VSK re-renders reset the scroll). */
  var mobileScrollTries = 0;
  function mobileShowSelected() {
    if (window.innerWidth >= DESKTOP_MIN || mobileScrollTries > 20) return;
    mobileScrollTries++;
    var ul = findList();
    if (!ul || ul.scrollWidth <= ul.clientWidth || ul.scrollLeft > 0) return;
    var sel = ul.querySelector('li .swatch-selected, li.swatch-selected');
    var li = sel && (sel.closest('li.swatch-view-item') || sel);
    if (!li) return;
    var ulRect = ul.getBoundingClientRect();
    var liRect = li.getBoundingClientRect();
    /* scrollLeft is 0 here, so rect offsets are content offsets. */
    var target = (liRect.left - ulRect.left) - (ul.clientWidth - liRect.width) / 2;
    if (target > 0) ul.scrollLeft = target;
  }

  function start() {
    if (window.innerWidth < DESKTOP_MIN) { mobileShowSelected(); return; }
    render();
    // Observe a stable ancestor so we re-assert after any VSK re-render.
    var wrap = document.querySelector('.swatches') || document.querySelector('.product-detail') || document.body;
    if (wrap && !wrap.__croSwatchObs) {
      wrap.__croSwatchObs = true;
      try {
        new MutationObserver(function () { render(); })
          .observe(wrap, { childList: true, subtree: true });
      } catch (e) {}
      var rt;
      window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(render, 150); });
    }
  }

  function boot() {
    start();
    var n = 0;
    var iv = setInterval(function () { start(); if (++n > 80) clearInterval(iv); }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
